import React from 'react';
import { func, number, object, shape, node, string } from 'prop-types';
import classNames from 'classnames';
import { getIn } from 'final-form';
import { useForm, useFormState } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import {
  HiOutlineCheck,
  HiOutlineExclamationTriangle,
  HiOutlinePencil,
  HiOutlinePlus,
  HiOutlineTrash,
} from 'react-icons/hi2';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { richTextToText } from '../../util/validators';
import { Button } from '../../components';

import css from './EditableItemList.css';

// The open draft lives in the form values at `draftName` as
// `{ ...item, __index }`: `__index` is the edited item's index, or null for a
// new item. A null draft means no editor is open.
const INDEX_KEY = '__index';

const stripIndex = draft => {
  const { [INDEX_KEY]: omitted, ...item } = draft; // eslint-disable-line no-unused-vars
  return item;
};

const isNewDraft = draft => draft[INDEX_KEY] === null || draft[INDEX_KEY] === undefined;

// Final Form drops a field's key when its input is cleared, so a missing key,
// null and '' all count as the same empty value. So does rich text without
// visible text: an empty Quill editor reports "<p><br></p>".
const isEmptyValue = value =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && richTextToText(value).trim() === '');
const isSameValue = (a, b) => (isEmptyValue(a) && isEmptyValue(b)) || isEqual(a, b);

/**
 * True when the open draft differs from what it was opened from: the saved
 * item at its index, or `emptyItem` for a new item.
 */
export const isDraftDirty = (draft, items, emptyItem) => {
  if (!draft) {
    return false;
  }
  const item = stripIndex(draft);
  const reference = (isNewDraft(draft) ? emptyItem : (items || [])[draft[INDEX_KEY]]) || {};
  const keys = Object.keys({ ...reference, ...item });
  return keys.some(key => !isSameValue(item[key], reference[key]));
};

/**
 * Picks the state-dependent copy for `EditableItemList`'s `messages`: a new
 * item and a saved item being edited use different tag and button labels, and
 * the add button reads differently with no items. Pass already translated
 * strings; returns `{ draftTag, save, discard, add }`.
 */
export const stateMessages = ({ draft, itemCount, newItem, savedItem, add, addAnother }) => {
  const isEditingSaved = !!draft && !isNewDraft(draft);
  const { tag, save, discard } = isEditingSaved ? savedItem : newItem;
  return {
    draftTag: tag,
    save,
    discard,
    add: itemCount === 0 ? add : addAnother,
  };
};

const hasErrors = errors =>
  typeof errors === 'string' ||
  (!!errors && typeof errors === 'object' && Object.values(errors).some(hasErrors));

const SummaryCard = props => {
  const { item, index, isValid, isLocked, renderSummary, onEdit, onDelete, intl } = props;
  return (
    <div className={css.card}>
      {isValid ? (
        <span className={css.statusReady}>
          <HiOutlineCheck className={css.statusIcon} />
        </span>
      ) : (
        // Legacy items can be incomplete (e.g. an old package without a title).
        <span
          className={css.statusIncomplete}
          role="img"
          aria-label={intl.formatMessage({ id: 'EditableItemList.needsEdits' })}
          title={intl.formatMessage({ id: 'EditableItemList.needsEdits' })}
        >
          <HiOutlineExclamationTriangle className={css.statusIcon} />
        </span>
      )}
      <div className={css.cardContent}>{renderSummary(item, index)}</div>
      <div className={classNames(css.cardActions, { [css.cardActionsLocked]: isLocked })}>
        <Button type="button" rootClassName={css.ghostButton} onClick={onEdit} disabled={isLocked}>
          <HiOutlinePencil className={css.ghostButtonIcon} />
          <FormattedMessage id="EditableItemList.edit" />
        </Button>
        <Button
          type="button"
          rootClassName={css.iconButton}
          onClick={onDelete}
          disabled={isLocked}
          aria-label={intl.formatMessage({ id: 'EditableItemList.delete' })}
        >
          <HiOutlineTrash className={css.iconButtonIcon} />
        </Button>
      </div>
    </div>
  );
};

const Editor = props => {
  const { title, draftTag, draftName, index, renderEditorFields, messages, canSave } = props;
  const { onSave, onDiscard } = props;
  return (
    <div className={css.editor}>
      <div className={css.editorHeader}>
        <span className={css.editorTitle}>
          <HiOutlinePencil className={css.editorTitleIcon} />
          {title}
        </span>
        <span className={css.draftTag}>{draftTag}</span>
      </div>
      <div className={css.editorFields}>{renderEditorFields({ draftName, index })}</div>
      <div className={css.editorFooter}>
        <Button type="button" rootClassName={css.discardButton} onClick={onDiscard}>
          {messages.discard}
        </Button>
        <Button type="button" rootClassName={css.saveButton} onClick={onSave} disabled={!canSave}>
          {messages.save}
        </Button>
      </div>
    </div>
  );
};

const EditableItemListComponent = props => {
  const {
    className,
    name,
    draftName,
    maxItems,
    emptyItem,
    isItemValid,
    renderSummary,
    renderEditorFields,
    messages,
    onSave,
    intl,
  } = props;

  const form = useForm();
  const { values, errors } = useFormState({ subscription: { values: true, errors: true } });
  const items = getIn(values, name) || [];
  const draft = getIn(values, draftName) || null;
  const isEditing = !!draft;
  const editingIndex = isEditing && !isNewDraft(draft) ? draft[INDEX_KEY] : null;
  const isFull = items.length >= maxItems;

  const openEditor = index =>
    form.change(
      draftName,
      index === null ? { ...emptyItem, [INDEX_KEY]: null } : { ...items[index], [INDEX_KEY]: index }
    );
  const closeEditor = () => form.change(draftName, null);
  const saveDraft = () => {
    const item = stripIndex(draft);
    const nextItems = isNewDraft(draft)
      ? [...items, item]
      : items.map((existing, i) => (i === draft[INDEX_KEY] ? item : existing));
    form.batch(() => {
      form.change(name, nextItems);
      form.change(draftName, null);
    });
    if (onSave) {
      onSave();
    }
  };
  const deleteItem = index =>
    form.change(
      name,
      items.filter((item, i) => i !== index)
    );

  const renderEditor = () => {
    const isNew = isNewDraft(draft);
    return (
      <Editor
        title={isNew ? messages.newTitle(items.length + 1) : messages.editTitle(editingIndex + 1)}
        draftTag={messages.draftTag}
        draftName={draftName}
        index={isNew ? null : editingIndex}
        renderEditorFields={renderEditorFields}
        messages={messages}
        canSave={isItemValid(stripIndex(draft)) && !hasErrors(getIn(errors, draftName))}
        onSave={saveDraft}
        onDiscard={closeEditor}
      />
    );
  };

  return (
    <div className={classNames(css.root, className)}>
      {items.map((item, index) =>
        isEditing && index === editingIndex ? (
          <React.Fragment key={index}>{renderEditor()}</React.Fragment>
        ) : (
          <SummaryCard
            key={index}
            item={item}
            index={index}
            isValid={isItemValid(item)}
            isLocked={isEditing}
            renderSummary={renderSummary}
            onEdit={() => openEditor(index)}
            onDelete={() => deleteItem(index)}
            intl={intl}
          />
        )
      )}
      {isEditing && isNewDraft(draft) ? renderEditor() : null}
      {!isEditing && !isFull ? (
        <Button type="button" rootClassName={css.addButton} onClick={() => openEditor(null)}>
          <HiOutlinePlus className={css.addButtonIcon} />
          {messages.add}
        </Button>
      ) : null}
      {isFull && messages.limitNote ? <p className={css.limitNote}>{messages.limitNote}</p> : null}
    </div>
  );
};

EditableItemListComponent.defaultProps = {
  className: null,
  onSave: null,
};

EditableItemListComponent.propTypes = {
  className: string,
  // The committed array field, e.g. 'packages'.
  name: string.isRequired,
  // The draft field, e.g. 'packageDraft'.
  draftName: string.isRequired,
  maxItems: number.isRequired,
  emptyItem: object.isRequired,
  isItemValid: func.isRequired,
  // (item, index) => node
  renderSummary: func.isRequired,
  // ({ draftName, index }) => node; index is null for a new item.
  renderEditorFields: func.isRequired,
  messages: shape({
    newTitle: func.isRequired,
    editTitle: func.isRequired,
    save: node.isRequired,
    discard: node.isRequired,
    add: node.isRequired,
    draftTag: node.isRequired,
    limitNote: node,
  }).isRequired,
  // Called once the open editor's item is saved into the list.
  onSave: func,
  intl: intlShape.isRequired,
};

const EditableItemList = injectIntl(EditableItemListComponent);

export default EditableItemList;
